import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, withHandlers, withState, withProps } from 'recompose';
import { omit } from 'lodash';

import { withClickOutside } from 'decorators';
import { GoogleMap } from 'components/core/GoogleMap';
import { SearchMarker } from 'components/search/SearchMap';
import { isLarge } from 'utils/breakpoints';
import { Text } from 'components/core/Typography';

import './SearchMap.scss';

export const enhance = compose(
	withState('searchFromMove', 'setSearchFromMove', false),
	withHandlers({
		handleMapMove:
			({ handleSearch, searchParams, setSearchFromMove }) =>
			(location) => {
				if (location) {
					setSearchFromMove(true);
					let locationParams = {
						...searchParams,
						location: `${location.latitude}, ${location.longitude}`,
					};
					locationParams = omit(locationParams, ['page', 'pageType']);
					handleSearch({ locationParams });
				}
			},
	}),
	// setting the props needed for the withClickOutside decorator
	withProps(
		({
			handleFacilityDeselected,
			handlePinDeselected,
			pinSelected,
			isNewSearchVariant, // [sf16978Variation]
		}) => ({
			outsideClickHandler: () => {
				handleFacilityDeselected();
				handlePinDeselected();
			},
			outsideParent: isNewSearchVariant // [sf16978Variation]
				? '.search-overlay-card'
				: '.new-facility-card',
			outsideCondition: pinSelected,
		}),
	),
	withClickOutside,
);

export function SearchMap({
	className,
	facilities,
	baseUrl,
	facilitySelected,
	handleFacilityDeselected,
	handleFacilitySelected,
	handlePinSelected,
	handleMapMove,
	location,
	searchType,
	pinSelected,
	handlePinDeselected,
	searchFromMove,
	setSearchFromMove,
	searchParams,
	cloudfrontUrl,
	isNewSearchVariant, // [sf16978Variation]
	onScrollToFacilityCard,
}) {
	const variantAttr = isNewSearchVariant ? 'new' : 'old'; // [sf16978Variation]
	return (
		<div
			className={classnames('search-map', className)}
			data-variant={variantAttr}
		>
			<noscript>
				<Text
					weight="bold"
					className="noscript-message"
				>
					Please enable JavaScript to use the interactive map.
				</Text>
			</noscript>

			<GoogleMap
				center={location}
				locationBounds={facilities.map((facility) => facility.location)}
				onMove={handleMapMove}
				radius={5}
				searchFromMove={searchFromMove}
				setSearchFromMove={setSearchFromMove}
			>
				{facilities.map((facility) => {
					const isDesktop =
						typeof window !== 'undefined' &&
						isLarge(window.innerWidth);
					const showMiniCard =
						facilitySelected === facility.id &&
						pinSelected &&
						(!isNewSearchVariant || !isDesktop); // suppress on desktop in new variant [sf16978Variation]
					return (
						<SearchMarker
							key={`search-marker-${facility.id}`}
							facility={facility}
							baseUrl={baseUrl}
							cloudfrontUrl={cloudfrontUrl}
							handleFacilitySelected={handleFacilitySelected}
							handleFacilityDeselected={handleFacilityDeselected}
							handlePinSelected={handlePinSelected}
							handlePinDeselected={handlePinDeselected}
							location={location}
							pinSelected={pinSelected}
							selected={facilitySelected === facility.id}
							// the mini facility card should only be shown if the pin is selected
							// but not if the facility card in the search results is the one selected
							showMiniCard={showMiniCard}
							searchType={searchType}
							searchParams={searchParams}
							isNewSearchVariant={isNewSearchVariant} // [sf16978Variation]
							onScrollToFacilityCard={onScrollToFacilityCard}
						/>
					);
				})}
			</GoogleMap>
		</div>
	);
}

SearchMap.propTypes = {
	className: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	handleMapMove: PropTypes.func,
	location: PropTypes.object.isRequired,
	searchFromMove: PropTypes.bool,
	setSearchFromMove: PropTypes.func,
	searchType: PropTypes.string,
	pinSelected: PropTypes.bool,
	handlePinDeselected: PropTypes.func,
	searchParams: PropTypes.object,
	cloudfrontUrl: PropTypes.string,
	isNewSearchVariant: PropTypes.bool,
	onScrollToFacilityCard: PropTypes.func,
};

export default enhance(SearchMap);
